"use client";

import { useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

interface MouseParallaxOptions {
  strength?: number;
  maxOffset?: number;
}

export function useMouseParallax({
  strength = 0.02,
  maxOffset = 12,
}: MouseParallaxOptions = {}) {
  const reduced = useReducedMotion();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const updateEnabled = () => setEnabled(media.matches && !reduced);
    updateEnabled();
    media.addEventListener("change", updateEnabled);
    return () => media.removeEventListener("change", updateEnabled);
  }, [reduced]);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (event: MouseEvent) => {
      const x = (event.clientX - window.innerWidth / 2) * strength;
      const y = (event.clientY - window.innerHeight / 2) * strength;
      setOffset({
        x: Math.max(-maxOffset, Math.min(maxOffset, x)),
        y: Math.max(-maxOffset, Math.min(maxOffset, y)),
      });
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [enabled, strength, maxOffset]);

  if (!enabled) {
    return { x: 0, y: 0 };
  }

  return offset;
}
